import {
  Button,
  ButtonGroup,
  Divider,
  GridItem,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spacer,
  Tag,
  TagCloseButton,
  TagLabel,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  faChevronLeft,
  faChevronRight,
  faRulerHorizontal,
} from '@fortawesome/free-solid-svg-icons';
import { sum } from 'lodash';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import GhostModeButton from '../GhostModeButton';
import SheetShapeLabelsButton from '../SheetShapeLabelsButton';
import { useSheetViewer } from '../SheetViewer';
import SparkelIcon from '../icon/SparkelIcon';
import { useViewerMode, ViewerMode } from '../viewer-mode';
import { useSheetShapes } from '../../../hooks/sheet-shapes';
import ProtanVisualsToggleButtons from '../ProtanVisualsToggleButtons';
import { SheetCalibration } from './SheetCalibration';
import { useUserTenant } from 'src/services/auth-info';

interface SheetViewerInteractionsProps {
  projectId: string;
  readOnly?: boolean;
}

export default function SheetViewerInteractions({
  projectId,
  readOnly,
}: SheetViewerInteractionsProps): ReactElement | null {
  const {
    page,
    setPageNumber,
    pageNumber,
    numberOfPages,
    isMeasuring,
    setIsMeasuring,
    calibration: { calibration },
  } = useSheetViewer();

  const {
    drawing: { deactivate },
  } = useSheetShapes();
  const { viewerMode } = useViewerMode();
  const {
    visibilityManager: {
      setIsolatedElements,
      setHiddenElements,
      hiddenElements,
      isolatedElements,
    },
  } = useSheetViewer();
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  const { t } = useTranslation('project');

  const { isProtan } = useUserTenant();

  return viewerMode === ViewerMode.Sheets && page ? (
    <GridItem
      area="viewer-interactions"
      display="flex"
      pointerEvents="none"
      paddingTop={2}
      zIndex={3}
      // This is a hack to make sure the buttons are clickable, but not the spacer, which allows the user to click through to the viewer
      sx={{
        '> *:not(:empty)': {
          pointerEvents: 'auto',
        },
        '@media (max-width: 1200px)': {
          paddingRight: 2,
        },
      }}
    >
      <ButtonGroup size="xs" mr={2}>
        <IconButton
          icon={<SparkelIcon icon={faChevronLeft} />}
          variant="solid-no-transparent"
          aria-label="Go to previous page"
          colorScheme="gray"
          isDisabled={pageNumber === 1}
          onClick={() => {
            setPageNumber(pageNumber - 1);
          }}
        />
        <Menu>
          <MenuButton
            as={Button}
            colorScheme="gray"
            variant={'solid-no-transparent'}
            isDisabled={numberOfPages === 1}
          >
            {t('viewer-interactions.page')} {pageNumber} / {numberOfPages}
          </MenuButton>
          <MenuList>
            <MenuOptionGroup type="radio" value={`${pageNumber}`}>
              {[...Array(numberOfPages)].map((_, i) => (
                <MenuItemOption
                  key={i + 1}
                  value={`${i + 1}`}
                  onClick={() => {
                    setPageNumber(i + 1);
                  }}
                >
                  {t('viewer-interactions.page')} {i + 1}
                </MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
        </Menu>
        <IconButton
          icon={<SparkelIcon icon={faChevronRight} />}
          variant="solid-no-transparent"
          colorScheme="gray"
          aria-label="Go to next page"
          isDisabled={pageNumber === numberOfPages}
          onClick={() => {
            setPageNumber(pageNumber + 1);
          }}
        />
        {!readOnly ? <SheetCalibration projectId={projectId} /> : null}
        <Divider borderColor={borderColor} orientation="vertical" />
        <GhostModeButton />
        <SheetShapeLabelsButton />
        {isProtan && <ProtanVisualsToggleButtons />}
        <Tooltip
          label={
            calibration
              ? t('viewer-interactions.measure-toggle')
              : t('viewer-interactions.calibration-required')
          }
        >
          <IconButton
            aria-label="Measure tool"
            icon={<SparkelIcon icon={faRulerHorizontal} />}
            isRound
            isActive={isMeasuring}
            variant="solid-no-transparent"
            colorScheme="gray"
            isDisabled={!calibration}
            size="xs"
            onClick={() => {
              setIsMeasuring(!isMeasuring);
              deactivate();
            }}
          />
        </Tooltip>
      </ButtonGroup>
      <Spacer />
      <HStack spacing={2}>
        {numIsolatedElements > 0 ? (
          <Tag size="sm" borderRadius="full" variant="toggle">
            <TagLabel>
              {t('viewer-interactions.isolated-elements', {
                numIsolatedElements,
              })}
            </TagLabel>
            <TagCloseButton
              onClick={() => setIsolatedElements({})}
              aria-label={t('viewer-interactions.clear-isolated')}
            />
          </Tag>
        ) : null}
        {numHiddenElements > 0 ? (
          <Tag size="sm" borderRadius="full" variant="toggle">
            <TagLabel>
              {t('viewer-interactions.hidden-elements', { numHiddenElements })}
            </TagLabel>
            <TagCloseButton
              onClick={() => setHiddenElements({})}
              aria-label={t('viewer-interactions.clear-hidden')}
            />
          </Tag>
        ) : null}
      </HStack>
    </GridItem>
  ) : null;
}
